import { useSearchParams } from "react-router-dom";
import { Calendar, Phone, ExternalLink } from "lucide-react";
import { BUSINESS } from "@/config";
import { SERVICES } from "@/data/site";
import SEO from "@/components/SEO";
import { MaskedLines, Reveal } from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import { track } from "@/lib/analytics";

export default function Book() {
  const [params] = useSearchParams();
  const service = params.get("service");
  const svcName = service && SERVICES[service] ? SERVICES[service].name : null;

  return (
    <>
      <SEO
        title="Book Online — Hell's Kitchen Salon, NYC"
        description="Book your hair, nail, facial or waxing appointment at Maison Neuf in Hell's Kitchen, Manhattan. Reserve online or call and text us to schedule."
      />
      <section className="mx-auto max-w-[1400px] px-5 pt-36 pb-16 md:px-10 md:pt-44" data-testid="book-hero">
        <p className="label mb-6 text-wine">Book Online</p>
        <h1 className="max-w-4xl font-display text-5xl leading-[1.0] md:text-8xl">
          <MaskedLines lines={["Reserve your", "chair."]} />
        </h1>
        {svcName && <p className="mt-6 font-mono text-sm uppercase tracking-[0.16em] text-muted-foreground">Selected studio · {svcName}</p>}
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-14 px-5 pb-24 md:grid-cols-12 md:px-10" data-testid="book-body">
        <div className="md:col-span-5">
          <Reveal>
            <div className="border border-border p-8">
              <Calendar className="h-7 w-7 text-wine" strokeWidth={1.5} />
              <h2 className="mt-5 font-display text-2xl">Instant online booking</h2>
              <p className="mt-3 text-muted-foreground">
                Our live booking calendar connects directly to our chairs. Choose your service, specialist, and time in under a minute.
              </p>
              {BUSINESS.bookingUrl ? (
                <a href={BUSINESS.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="book-engine-link" onClick={() => track("book_online_click", { location: "book_page", destination: "external_calendar" })} className="mt-6 inline-flex items-center gap-2 bg-primary px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] text-primary-foreground">
                  Open booking calendar <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                </a>
              ) : (
                <div className="mt-6 border border-dashed border-border p-5">
                  <p className="text-sm text-muted-foreground">
                    Online booking is being connected to our scheduling system. In the meantime, call or text and we'll book you in right away — or send an inquiry below.
                  </p>
                </div>
              )}
              <a href={BUSINESS.phoneHref} data-testid="book-call-link" onClick={() => track("phone_click", { location: "book_page" })} className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-foreground hover:text-foreground link-underline">
                <Phone className="h-4 w-4" strokeWidth={1.5} /> Call / Text · {BUSINESS.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7 md:border-l md:border-border md:pl-12">
          <Reveal>
            <p className="label mb-3 text-wine">Request an appointment</p>
            <p className="mb-8 max-w-md text-muted-foreground">Send us your preferred service and time, and we'll confirm by email or text within one business day.</p>
            <InquiryForm defaultService={service && SERVICES[service] ? service : ""} />
          </Reveal>
        </div>
      </section>
    </>
  );
}
